// =====================================================================
//  CÁLCULO — espejo en TypeScript de las vistas v_puntaje_detalle y
//  v_puntaje
//
//  La duplicación es DELIBERADA y tiene un motivo concreto: cuando el
//  analista mueve un slider en la ficha, el puntaje tiene que responder
//  al instante, sin esperar un viaje al servidor. Postgres sigue siendo
//  la verdad; esto es una vista previa optimista.
//
//  Por eso calculo.test.ts compara criterio por criterio el resultado de
//  este archivo contra el de las vistas, sobre las 39 concesiones reales.
//  Si alguna de las dos cambia sin la otra, la prueba falla.
//
//  Aritmética del remate — todo sale del Código de Minería:
//    mínimo de subasta   = lo adeudado          (art. 149)
//    retiro del dueño    = lo adeudado × 2      (art. 149 inc. 2)
//    garantía del postor = lo adeudado del LOTE (art. 148)
// =====================================================================

import type { Concesion, Criterio } from '../tipos/dominio'
import { num } from './formato'

// ---------------------------------------------------------------------
//  Aritmética del remate
// ---------------------------------------------------------------------

/** Art. 149: el mínimo es el valor de las patentes adeudadas. */
export const minimoSubasta = (c: Concesion): number => num(c.monto_adeudado)

/** Art. 149 inc. 2: el dueño la saca pagando el doble. */
export const costoRetiroDueno = (c: Concesion): number => num(c.monto_adeudado) * 2

/**
 * Lo que realmente costaría: la postura definida, o el mínimo legal si
 * todavía no hay postura. Nunca se puede pagar menos que el mínimo.
 */
export const compromiso = (c: Concesion): number =>
  Math.max(num(c.postura_max), minimoSubasta(c))

export const estaEnRemate = (c: Concesion): boolean => c.estado === 'EN_REMATE'

/** Solo Alta y Media entran al cálculo de presupuesto. */
export const estaMarcada = (c: Concesion): boolean =>
  estaEnRemate(c) && (c.interes === 'ALTA' || c.interes === 'MEDIA')

// ---------------------------------------------------------------------
//  Percentil por UTM adeudadas
// ---------------------------------------------------------------------

/**
 * Réplica exacta de `percent_rank() over (partition by causa_id order by
 * utm_adeudadas)` de v_puntaje_detalle, incluida la guarda para una sola
 * concesión.
 *
 * Dos detalles que importan:
 *
 *  · Los EMPATES reciben el mismo percentil. El prototipo usaba el índice
 *    de orden (`i/(n-1)`), que separaba arbitrariamente concesiones con
 *    las mismas UTM. En estas nóminas hay empates en nueve valores
 *    distintos (10 UTM ×4, 0,4 UTM ×4, 40 UTM ×3, y seis pares más), así
 *    que afecta a 14 de las 39.
 *
 *  · Con UNA sola concesión en la causa el percentil sería 0, el peor
 *    posible, lo que no significa nada cuando no hay con qué comparar.
 *    Se usa el valor neutro, igual que hacía el prototipo.
 */
export function percentilesPorCausa(concesiones: Concesion[]): Map<number, number> {
  const porCausa = new Map<number, Concesion[]>()
  for (const c of concesiones) {
    const grupo = porCausa.get(c.causa_id)
    if (grupo) grupo.push(c)
    else porCausa.set(c.causa_id, [c])
  }

  const salida = new Map<number, number>()
  for (const grupo of porCausa.values()) {
    const n = grupo.length
    if (n === 1) {
      salida.set(grupo[0].id, 0.5)
      continue
    }
    const ord = [...grupo].sort((a, b) => num(a.utm_adeudadas) - num(b.utm_adeudadas))
    let i = 0
    while (i < ord.length) {
      const utm = num(ord[i].utm_adeudadas)
      let j = i
      while (j < ord.length && num(ord[j].utm_adeudadas) === utm) j++
      // percent_rank = (rank - 1) / (n - 1), con rank = i + 1
      const p = i / (n - 1)
      for (let k = i; k < j; k++) salida.set(ord[k].id, p)
      i = j
    }
  }
  return salida
}

// ---------------------------------------------------------------------
//  Puntaje
// ---------------------------------------------------------------------

export interface ContextoPuntaje {
  /** Percentil de esta concesión dentro de su causa, de percentilesPorCausa(). */
  percentil: number
  /** Presupuesto VIGENTE de la causa. 0 si nadie lo fijó. */
  presupuesto: number
}

/** El valor neutro de un criterio manual sin evaluar. */
export const NEUTRO = 3

/**
 * Valor 1–5 de un criterio para una concesión.
 *
 * Los tres automáticos se calculan; los cuatro manuales salen de
 * `manuales`, y si no están evaluados valen NEUTRO. Que el peso de un
 * criterio manual sin evaluar SÍ entre al denominador es lo que mantiene
 * los puntajes comparables entre una concesión evaluada y otra sin
 * evaluar (ver PLAN §5.11).
 */
export function valorCriterio(
  codigo: string,
  c: Concesion,
  ctx: ContextoPuntaje,
  manuales: Record<string, number> = {},
): number {
  switch (codigo) {
    // Escala y probabilidad de llegar a remate. Mide dos cosas a la vez
    // porque en la nómina son la misma variable: a más UTM, más grande el
    // activo y más caro le sale al dueño rescatarlo, porque debe pagar el
    // doble (art. 149).
    case 'escala':
      return 1 + 4 * ctx.percentil

    // Solidez del título: constituida frente a en trámite.
    case 'titulo':
      return c.rol_tipo === 'C' ? 5 : 2.5

    // Calce con el presupuesto, con saturación: 5 si el mínimo consume
    // menos del 15%, 1 si lo agota. Satura en vez de ser lineal para no
    // volver a ser el simple inverso de la escala.
    case 'calce': {
      const razon = ctx.presupuesto > 0 ? minimoSubasta(c) / ctx.presupuesto : 1
      return 5 - 4 * Math.min(1, Math.max(0, (razon - 0.15) / 0.85))
    }

    default:
      return manuales[codigo] ?? NEUTRO
  }
}

/** Puntaje 0–100. Réplica de v_puntaje. */
export function calcularPuntaje(
  c: Concesion,
  criterios: Criterio[],
  ctx: ContextoPuntaje,
  manuales: Record<string, number> = {},
): number {
  let suma = 0
  let pesos = 0
  for (const k of criterios) {
    if (!k.activo) continue
    suma += (valorCriterio(k.codigo, c, ctx, manuales) / 5) * k.peso
    pesos += k.peso
  }
  return pesos > 0 ? Math.round((suma / pesos) * 100) : 0
}

// ---------------------------------------------------------------------
//  Garantía del art. 148
// ---------------------------------------------------------------------

/**
 * Garantía a rendir sobre los lotes que tocamos.
 *
 * PLAN §5.2 — el art. 148 exige boleta de depósito por el total adeudado
 * de TODO el lote, no solo de las concesiones que nos interesan. Si la
 * mina que interesa viene amarrada a otras tres, hay que llevar la
 * garantía de las cuatro.
 *
 * Devuelve las dos cifras para poder mostrar la diferencia: `lotes` es lo
 * que de verdad hay que llevar, `soloMarcadas` es lo que se habría
 * presupuestado mirando únicamente lo marcado.
 */
export function garantiaPorLote(concesiones: Concesion[]): {
  lotes: number
  soloMarcadas: number
  lotesTocados: number
} {
  const clave = (c: Concesion) => c.lote ?? `Individual — ${c.rol_nacional}`

  const grupos = new Map<string, Concesion[]>()
  for (const c of concesiones) {
    const k = clave(c)
    const g = grupos.get(k)
    if (g) g.push(c)
    else grupos.set(k, [c])
  }

  let lotes = 0
  let soloMarcadas = 0
  let lotesTocados = 0

  for (const grupo of grupos.values()) {
    const marcadas = grupo.filter(estaMarcada)
    if (marcadas.length === 0) continue
    lotesTocados++
    // Art. 148: TODO el lote, marcado o no.
    lotes += grupo.reduce((s, c) => s + num(c.monto_adeudado), 0)
    soloMarcadas += marcadas.reduce((s, c) => s + num(c.monto_adeudado), 0)
  }

  return { lotes, soloMarcadas, lotesTocados }
}

/** Suma de lo que costaría quedarse con todas las marcadas. */
export const comprometido = (concesiones: Concesion[]): number =>
  concesiones.filter(estaMarcada).reduce((s, c) => s + compromiso(c), 0)
